import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// ── Styles ────────────────────────────────────────────────────────────────────

const card = {
  background: 'white', border: '1px solid #d8e4dd',
  borderRadius: '10px', padding: '16px',
  boxShadow: '0 2px 12px rgba(24,47,38,0.07)'
};
const muted = { color: '#61726b', fontSize: '13px' };
const h2s = { margin: 0, color: '#064734', fontSize: '17px' };
const input = {
  border: '1px solid #d8e4dd', borderRadius: '8px',
  padding: '9px 11px', background: 'white', color: '#12231d', fontWeight: 700
};

function euro(v) {
  return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CFG = {
  pruefen:     { label: 'Prüfen',          color: '#2563eb', bg: '#eff6ff' },
  notwendig:   { label: 'Notwendig',        color: '#7c3aed', bg: '#f5f3ff' },
  behalten:    { label: 'Behalten',         color: '#0a6f4f', bg: '#ecfdf5' },
  kuendigen:   { label: 'Kündigen',         color: '#b42318', bg: '#fff1ef' },
  kein_vertrag:{ label: 'Kein Vertrag',     color: '#64748b', bg: '#f8fafc' },
  spaeter:     { label: 'Später prüfen',    color: '#a16207', bg: '#fffbeb' },
  bestaetigt:  { label: 'Bestätigt',        color: '#064734', bg: '#dff3e9' },
};
function statusColor(s) { return STATUS_CFG[s]?.color || '#64748b'; }
function statusLabel(s) { return STATUS_CFG[s]?.label || s; }

const CONF_CFG = {
  HIGH:   { label: 'HIGH',   color: '#0a6f4f', bg: '#ecfdf5', text: 'Starke Vertragssignale' },
  MEDIUM: { label: 'MEDIUM', color: '#a16207', bg: '#fffbeb', text: 'Mehrere Hinweise, Prüfung empfohlen' },
  LOW:    { label: 'LOW',    color: '#64748b', bg: '#f8fafc', text: 'Muster erkannt, Vertragscharakter unsicher' },
};

const KSTATUS_CFG = {
  vorgemerkt: { label: 'Kündigung vorgemerkt', color: '#a16207', bg: '#fffbeb' },
  vorbereitet: { label: 'Brief vorbereitet',   color: '#1d4ed8', bg: '#eff6ff' },
  versendet:   { label: 'Kündigung versendet', color: '#b42318', bg: '#fff1ef' },
  bestaetigt:  { label: 'Bestätigt',           color: '#064734', bg: '#dff3e9' },
  beendet:     { label: 'Beendet',             color: '#475569', bg: '#f1f5f9' },
};
function KStatusBadge({ s }) {
  if (!s || !KSTATUS_CFG[s]) return null;
  const cfg = KSTATUS_CFG[s];
  return (
    <span style={{ background: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11, padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

const INTERVALL_LABEL = {
  weekly: 'Wöchentlich', biweekly: 'Zweiwöchentlich', monthly: 'Monatlich',
  quarterly: 'Quartalsweise', halfyear: 'Halbjährlich', yearly: 'Jährlich',
};

function intervallLabel(days) {
  if (days <= 10) return 'Wöchentlich';
  if (days <= 20) return 'Zweiwöchentlich';
  if (days <= 45) return 'Monatlich';
  if (days <= 100) return 'Quartalsweise';
  if (days <= 200) return 'Halbjährlich';
  return 'Jährlich';
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ConfBadge({ c }) {
  const cfg = CONF_CFG[c] || CONF_CFG.LOW;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      fontWeight: 700, fontSize: 11, padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap'
    }}>{cfg.label}</span>
  );
}

function StatusBadge({ s }) {
  return (
    <span style={{
      background: STATUS_CFG[s]?.bg || '#f8fafc', color: statusColor(s),
      fontWeight: 700, fontSize: 11, padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap'
    }}>{statusLabel(s)}</span>
  );
}

function Btn({ color, disabled, onClick, children, outline }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        background: outline ? 'white' : color,
        color: outline ? color : 'white',
        border: `1.5px solid ${color}`,
        borderRadius: '8px', padding: '8px 10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 700, fontSize: '13px',
        opacity: disabled ? 0.6 : 1, whiteSpace: 'nowrap'
      }}
    >{children}</button>
  );
}

function InfoBox({ label, value, color }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '9px' }}>
      <div style={{ ...muted, fontSize: '10px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ color: color || '#12231d', fontWeight: 800, marginTop: '3px', fontSize: '13px' }}>{value || '–'}</div>
    </div>
  );
}

function Stat({ title, value, hint, color = '#1d4ed8' }) {
  return (
    <div style={card}>
      <div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{title}</div>
      <div style={{ color, fontSize: '22px', fontWeight: 900, marginTop: '5px' }}>{value}</div>
      <div style={{ ...muted, marginTop: '3px' }}>{hint}</div>
    </div>
  );
}

function Notice({ tone, children }) {
  const red = tone === 'red';
  const green = tone === 'green';
  const c = red ? '#9f1d12' : green ? '#064734' : '#a16207';
  const bg = red ? '#fff1ef' : green ? '#dff3e9' : '#fffbeb';
  const bc = red ? '#ffc9c2' : green ? '#a7d9c2' : '#fde68a';
  return (
    <div style={{ background: bg, color: c, border: `1px solid ${bc}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontWeight: 700 }}>
      {children}
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────

function ConfirmDialog({ item, onConfirm, onCancel }) {
  const [notiz, setNotiz] = useState(item.review_notiz || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit() {
    setBusy(true); setErr('');
    try {
      await onConfirm({ notiz });
    } catch (e) {
      setErr(e.message || 'Fehler');
      setBusy(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px'
    }}>
      <div style={{ ...card, maxWidth: '480px', width: '100%' }}>
        <h2 style={{ ...h2s, marginBottom: '8px' }}>Als Vertrag bestätigen</h2>
        <p style={muted}>Dieser Radar-Kandidat wird als bestätigter Vertrag gespeichert.</p>
        <div style={{ margin: '16px 0', display: 'grid', gap: '8px' }}>
          <InfoBox label="Anbieter" value={item.empfaenger} />
          <InfoBox label="Erkannte Bezeichnung" value={item.bezeichnung} />
          <InfoBox label="Durchschnittlicher Betrag" value={euro(item.durchschnitt)} />
          <InfoBox label="Intervall" value={intervallLabel(item.intervall_tage)} />
          <InfoBox label="Geschätzte Jahreskosten" value={euro(item.jahreswert)} />
          <InfoBox label="Confidence" value={item.confidence} color={CONF_CFG[item.confidence]?.color} />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ ...muted, fontWeight: 700 }}>Notiz (optional)</label>
          <textarea
            value={notiz}
            onChange={e => setNotiz(e.target.value)}
            maxLength={500}
            rows={2}
            style={{ ...input, width: '100%', marginTop: '6px', resize: 'vertical', boxSizing: 'border-box' }}
            placeholder="Eigene Notiz zum Vertrag"
          />
        </div>
        {err && <Notice tone="red">{err}</Notice>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <Btn color="#64748b" outline onClick={onCancel} disabled={busy}>Abbrechen</Btn>
          <Btn color="#064734" onClick={submit} disabled={busy}>{busy ? '…' : 'Jetzt bestätigen'}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Detail panel (expanded inline) ───────────────────────────────────────────

function DetailPanel({ item }) {
  const gruende = item._erkennung_gruende || [];
  const fin = item._finanzierung;
  const preis = item._preis_aenderung;

  return (
    <div style={{ borderTop: '1px solid #d8e4dd', marginTop: '14px', paddingTop: '14px' }}>
      {/* Warum erkannt */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontWeight: 800, color: '#064734', fontSize: '13px', marginBottom: '6px' }}>Warum als Vertrag erkannt?</div>
        {gruende.length > 0
          ? <ul style={{ margin: 0, padding: '0 0 0 18px' }}>
              {gruende.map((g, i) => <li key={i} style={{ ...muted, marginBottom: '3px' }}>{g}</li>)}
            </ul>
          : <p style={muted}>Keine Erkennungsdetails verfügbar.</p>}
        {item._vz_sample && (
          <div style={{ marginTop: '8px', background: '#f8fafc', borderRadius: '6px', padding: '8px', fontSize: '12px', color: '#61726b', fontFamily: 'monospace' }}>
            Beispiel-Verwendungszweck: {item._vz_sample}
          </div>
        )}
      </div>

      {/* Confidence-Erklärung */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontWeight: 800, color: '#064734', fontSize: '13px', marginBottom: '4px' }}>Confidence: {item.confidence}</div>
        <p style={muted}>{CONF_CFG[item.confidence]?.text || 'Keine Erklärung verfügbar.'}</p>
      </div>

      {/* Finanzierung */}
      {fin && (
        <div style={{ background: '#fffbeb', borderRadius: '8px', padding: '10px', marginBottom: '10px', border: '1px solid #fde68a' }}>
          <div style={{ fontWeight: 800, color: '#a16207', fontSize: '13px', marginBottom: '4px' }}>Finanzierungsrate erkannt</div>
          <p style={{ ...muted, margin: 0 }}>
            Rate {fin.aktuelle_rate} von {fin.gesamtraten} — noch {fin.verbleibend} Rate{fin.verbleibend !== 1 ? 'n' : ''} verbleibend
          </p>
        </div>
      )}

      {/* Preisänderung */}
      {preis && (
        <div style={{ background: '#fff1ef', borderRadius: '8px', padding: '10px', marginBottom: '10px', border: '1px solid #ffc9c2' }}>
          <div style={{ fontWeight: 800, color: '#b42318', fontSize: '13px', marginBottom: '4px' }}>
            Preisänderung erkannt ({preis.prozent > 0 ? '+' : ''}{preis.prozent} %)
          </div>
          <p style={{ ...muted, margin: 0 }}>
            Früher Ø {euro(preis.alt)} → Jetzt Ø {euro(preis.neu)}
          </p>
        </div>
      )}

      {/* Buchungshistorie-Info */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px' }}>
        <InfoBox label="Erste Buchung" value={item.erster} />
        <InfoBox label="Letzte Buchung" value={item.letzter} />
        <InfoBox label="Anzahl Buchungen" value={item.anzahl} />
        <InfoBox label="Nächste Erwartung" value={item.naechste_erwartung} />
        {item.min_betrag !== item.max_betrag && (
          <InfoBox label="Betragsbereich" value={`${euro(item.min_betrag)} – ${euro(item.max_betrag)}`} />
        )}
      </div>

      {item.review_notiz && (
        <div style={{ marginTop: '10px', background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
          <div style={{ ...muted, fontWeight: 700, marginBottom: '3px' }}>Notiz</div>
          <div style={{ fontSize: '13px', color: '#12231d' }}>{item.review_notiz}</div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ContractsPage() {
  const nav = useNavigate();
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('offen'); // offen = all except kein_vertrag
  const [confFilter, setConfFilter] = useState('all');
  const [busyKey, setBusyKey] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [confirmItem, setConfirmItem] = useState(null);
  const [notizEdit, setNotizEdit] = useState({}); // key → text

  useEffect(() => { load(); }, [months]);

  async function load() {
    setError('');
    try {
      const r = await api.get('/contracts/radar', { params: { monate: months } });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Vertragsradar konnte nicht geladen werden');
    }
  }

  async function save(item, patch) {
    const key = itemKey(item);
    setBusyKey(key); setError(''); setMessage('');
    try {
      await api.post('/contracts/review', {
        empfaenger: item.empfaenger,
        kategorie: item.kategorie,
        status: item.status,
        review_date: item.review_date || '',
        cancel_by: item.cancel_by || '',
        notiz: notizEdit[key] !== undefined ? notizEdit[key] : (item.review_notiz || ''),
        monate: months,
        ...patch
      });
      setMessage(`${item.bezeichnung}: Gespeichert.`);
      setTimeout(() => setMessage(''), 4000);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Status konnte nicht gespeichert werden');
    } finally { setBusyKey(''); }
  }

  async function handleConfirm(item, { notiz }) {
    try {
      await api.post('/contracts/confirm', {
        empfaenger: item.empfaenger,
        kategorie: item.kategorie,
        bezeichnung: item.bezeichnung,
        durchschnitt: item.durchschnitt,
        intervall_tage: item.intervall_tage,
        confidence: item.confidence,
        letzter: item.letzter,
        notiz
      });
      setConfirmItem(null);
      setMessage(`${item.bezeichnung}: Als Vertrag bestätigt.`);
      setTimeout(() => setMessage(''), 6000);
      await load();
    } catch (e) {
      throw new Error(e.response?.data?.error || e.message || 'Bestätigung fehlgeschlagen');
    }
  }

  function itemKey(item) {
    return `${item.empfaenger}|||${item.kategorie}`;
  }

  function toggleExpand(key) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const items = data?.items || [];
  const totals = data?.totals || {};

  const filtered = useMemo(() => {
    return items.filter(item => {
      // Confidence filter
      if (confFilter !== 'all' && item.confidence !== confFilter) return false;
      // Status filter
      if (filter === 'offen') return item.status !== 'kein_vertrag';
      if (filter === 'kein_vertrag') return item.status === 'kein_vertrag';
      if (filter === 'all') return true;
      return item.status === filter;
    });
  }, [items, filter, confFilter]);

  const zuPruefen = totals.pruefen || 0;
  const totalVisible = items.filter(i => i.status !== 'kein_vertrag').length;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '14px' }}>
        <h1 style={{ margin: 0, color: '#064734', fontSize: '24px', fontWeight: 900 }}>Abo- und Vertragsradar</h1>
        <p style={{ ...muted, margin: '4px 0 0' }}>
          Wiederkehrende Zahlungen prüfen — als Vertrag bestätigen, behalten, kündigen oder ablehnen.
        </p>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {message && <Notice tone="green">{message}</Notice>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '14px' }}>
        <Stat title="Erkannt" value={totalVisible} hint={`${months} Monate`} color="#1d4ed8" />
        <Stat title="Jahreswert" value={euro(totals.jahreswert)} hint="hochgerechnet" color="#0a6f4f" />
        <Stat title="Noch zu prüfen" value={zuPruefen} hint={`${totals.hoch || 0} hohe Priorität`} color="#a16207" />
        <Stat title="Kündigen" value={totals.kuendigen || 0} hint="markiert" color="#b42318" />
        <Stat title="Notwendig" value={totals.notwendig || 0} hint="bewusst gewollt" color="#7c3aed" />
        <Stat title="Bestätigt" value={totals.bestaetigt || 0} hint="als Vertrag" color="#064734" />
      </div>

      {/* Filters */}
      <div style={{ ...card, marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
        <div>
          <label style={{ ...muted, fontWeight: 700, marginRight: '6px' }}>Status:</label>
          <select value={filter} onChange={e => setFilter(e.target.value)} style={input}>
            <option value="offen">Offen (ohne Kein Vertrag)</option>
            <option value="all">Alle</option>
            <option value="pruefen">Prüfen ({totals.pruefen || 0})</option>
            <option value="notwendig">Notwendig ({totals.notwendig || 0})</option>
            <option value="behalten">Behalten ({totals.behalten || 0})</option>
            <option value="kuendigen">Kündigen ({totals.kuendigen || 0})</option>
            <option value="kein_vertrag">Kein Vertrag ({totals.kein_vertrag || 0})</option>
            <option value="spaeter">Später ({totals.spaeter || 0})</option>
            <option value="bestaetigt">Bestätigt ({totals.bestaetigt || 0})</option>
          </select>
        </div>
        <div>
          <label style={{ ...muted, fontWeight: 700, marginRight: '6px' }}>Confidence:</label>
          <select value={confFilter} onChange={e => setConfFilter(e.target.value)} style={input}>
            <option value="all">Alle</option>
            <option value="HIGH">HIGH</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="LOW">LOW</option>
          </select>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <label style={{ ...muted, fontWeight: 700, marginRight: '6px' }}>Zeitraum:</label>
          <select value={months} onChange={e => setMonths(Number(e.target.value))} style={input}>
            <option value={6}>6 Monate</option>
            <option value={12}>12 Monate</option>
            <option value={24}>24 Monate</option>
          </select>
        </div>
      </div>

      {/* Candidate cards */}
      <div style={{ display: 'grid', gap: '12px' }}>
        {filtered.map(item => {
          const key = itemKey(item);
          const busy = busyKey === key;
          const isOpen = expanded.has(key);
          const sc = statusColor(item.status);
          const preis = item._preis_aenderung;
          const fin = item._finanzierung;

          return (
            <article key={key} style={{
              ...card,
              borderLeftWidth: '4px',
              borderLeftColor: sc,
              borderColor: sc,
            }}>
              {/* Card header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <h2 style={{ margin: 0, color: '#12231d', fontSize: '16px', fontWeight: 900 }}>{item.bezeichnung}</h2>
                    <ConfBadge c={item.confidence} />
                    <StatusBadge s={item.status} />
                    <KStatusBadge s={item.kuendigungsstatus} />
                    {item.hat_dokument && <span style={{ background: '#dff3e9', color: '#064734', fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: 5 }}>📄 Bestätigung</span>}
                    {item.hat_reminder && <span style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: 5 }}>🔔 Erinnerung</span>}
                    {preis && <span style={{ background: '#fff1ef', color: '#b42318', fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: 5 }}>Preisänderung {preis.prozent > 0 ? '+' : ''}{preis.prozent}%</span>}
                    {fin && <span style={{ background: '#fffbeb', color: '#a16207', fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: 5 }}>Rate {fin.aktuelle_rate}/{fin.gesamtraten}</span>}
                  </div>
                  <p style={{ ...muted, margin: 0 }}>
                    {item.kategorie} · {intervallLabel(item.intervall_tage)} · {item.anzahl} Zahlungen · {item.monate} Monate
                  </p>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ color: sc, fontSize: '18px', fontWeight: 900 }}>{euro(item.jahreswert)}/Jahr</div>
                  <div style={muted}>Ø {euro(item.durchschnitt)}</div>
                </div>
              </div>

              {/* Key info row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', margin: '12px 0' }}>
                <InfoBox label="Nächste Zahlung" value={item.naechste_erwartung} />
                <InfoBox label="Letzte Buchung" value={item.letzter} />
                <InfoBox label="Jahreskosten (geschätzt)" value={euro(item.jahreswert)} color={sc} />
                <InfoBox label="Intervall" value={intervallLabel(item.intervall_tage)} />
                {item.kuendigungstermin_bestaetigt && (
                  <InfoBox label="Kündigen bis" value={item.kuendigungstermin_bestaetigt} color="#b42318" />
                )}
                {!item.kuendigungstermin_bestaetigt && item.kuendigungstermin_berechnet && item.kuendigungstermin_confidence !== 'LOW' && (
                  <InfoBox label={`Kündigen bis (${item.kuendigungstermin_confidence})`} value={item.kuendigungstermin_berechnet} color="#a16207" />
                )}
                {item.vertragsende_bestaetigt && (
                  <InfoBox label="Vertragsende" value={item.vertragsende_bestaetigt} color="#064734" />
                )}
                {item.next_reminder_date && (
                  <InfoBox label="Nächste Erinnerung" value={item.next_reminder_date} color="#1d4ed8" />
                )}
              </div>

              {/* Action buttons — 2-column grid for mobile */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '8px',
                marginBottom: '10px'
              }}>
                <Btn color="#7c3aed" disabled={busy} onClick={() => save(item, { status: 'notwendig' })}
                  outline={item.status !== 'notwendig'}>Notwendig</Btn>
                <Btn color="#0a6f4f" disabled={busy} onClick={() => save(item, { status: 'behalten' })}
                  outline={item.status !== 'behalten'}>Behalten</Btn>
                <Btn color="#b42318" disabled={busy} onClick={() => save(item, { status: 'kuendigen' })}
                  outline={item.status !== 'kuendigen'}>Kündigen</Btn>
                <Btn color="#64748b" disabled={busy} onClick={() => save(item, { status: 'kein_vertrag' })}
                  outline={item.status !== 'kein_vertrag'}>Kein Vertrag</Btn>
                <Btn color="#a16207" disabled={busy} onClick={() => save(item, { status: 'spaeter' })}
                  outline={item.status !== 'spaeter'}>Später</Btn>
                {item.status !== 'bestaetigt' && (
                  <Btn color="#064734" disabled={busy} onClick={() => setConfirmItem(item)}>Als Vertrag bestätigen</Btn>
                )}
              </div>

              {/* Secondary actions */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => toggleExpand(key)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '13px', fontWeight: 700, padding: '4px 0' }}
                >
                  {isOpen ? '▲ Details ausblenden' : '▼ Details & Erkennungsgründe'}
                </button>
                <button
                  onClick={() => nav(item.detail_url)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#61726b', fontSize: '13px', fontWeight: 700, padding: '4px 0' }}
                >
                  → Buchungshistorie
                </button>
                {item.series_id && (
                  <button
                    onClick={() => nav(`/contracts/${item.series_id}`)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#064734', fontSize: '13px', fontWeight: 700, padding: '4px 0' }}
                  >
                    → Vertragsdetails & Kündigung
                  </button>
                )}
              </div>

              {/* Notiz inline */}
              <div style={{ marginTop: '8px' }}>
                <input
                  type="text"
                  placeholder="Notiz hinzufügen …"
                  maxLength={200}
                  value={notizEdit[key] !== undefined ? notizEdit[key] : (item.review_notiz || '')}
                  onChange={e => setNotizEdit(prev => ({ ...prev, [key]: e.target.value }))}
                  onBlur={e => {
                    const val = e.target.value.trim();
                    const orig = item.review_notiz || '';
                    if (val !== orig) save(item, { notiz: val });
                  }}
                  style={{ ...input, width: '100%', boxSizing: 'border-box', fontSize: '13px' }}
                />
              </div>

              {/* Expanded detail */}
              {isOpen && <DetailPanel item={item} />}
            </article>
          );
        })}

        {filtered.length === 0 && (
          <div style={{ ...card, color: '#61726b', textAlign: 'center', padding: '32px' }}>
            Keine Einträge für diesen Filter.
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      {confirmItem && (
        <ConfirmDialog
          item={confirmItem}
          onConfirm={(opts) => handleConfirm(confirmItem, opts)}
          onCancel={() => setConfirmItem(null)}
        />
      )}
    </div>
  );
}
